import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import type { DownloadSurface } from 'generated/graphql';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';
import { useCallback } from 'react';
import { discardStaleGateOpen } from '~/modules-rocco/user/auth-gate/discard-stale-gate-open';

/** What the reader was trying to do when the gate stopped them. */
export type DownloadGateAction = 'download' | 'requestAccess';

/**
 * Enough of the project lookbook to redraw it on the success screen: the book
 * on the project's own picture, as the page's frame draws the pair.
 *
 * Strings rather than a node, because the cover is composed rather than
 * photographed — it cannot travel as an image src — and the intent has to
 * survive sessionStorage across a social round-trip.
 */
export interface LookbookArtwork {
    title: string;
    professional: string;
    /** The project's introduction, which the cover's paragraph closes on. */
    intro?: string;
    /** The banner the cover's photograph is drawn from. */
    imageSrc?: string;
    /** The project's own picture, the ground the book stands on. */
    frameSrc?: string;
}

export interface DownloadGateIntent {
    action: DownloadGateAction;
    /** Which of ArchiPro's download surfaces the reader was taking a file from. */
    surface: DownloadSurface;
    /**
     * Path of the page they were standing on. Carried rather than read at the
     * far end: the gate hands the file back after a social round-trip that
     * lands on a callback route, which is not where the file was asked for.
     */
    pagePath?: string;
    /** Direct file link. Absent for a request-access intent. */
    link?: string;
    /** File name for the success screen ("your file's ready to download"). */
    fileName?: string;
    /** The file's own record, where it has one, for the resume's own logging. */
    fileID?: number;
    /** Who published the file, which the download event carries as its label. */
    professionalTitle?: string;
    /**
     * The page the file is filed under — a product, or a project whose
     * lookbook this is. Both are library pages, and it is the id the
     * requestDownloadAccess mutation takes.
     *
     * Absent for a file filed under the professional rather than a page of
     * theirs: the profile's own downloads. The lead is recorded against the
     * professional alone.
     */
    libraryItemID?: number;
    professionalID: number;
    /** Hero of the item being downloaded, for the gate's brand pane. */
    itemImage?: string;
    /**
     * Draws the lookbook on the success screen in place of a thumbnail. A crop
     * of the project's banner is not the thing the reader asked for; the book
     * they are about to take is.
     */
    lookbookArtwork?: LookbookArtwork;
}

/**
 * The download the reader asked for before the gate stopped them, persisted so
 * it survives a social round-trip's full reload the same way the enquiry draft
 * does. Session-scoped: an abandoned intent must not follow them into a later
 * visit and start a download they have forgotten asking for.
 */
export const DOWNLOAD_GATE_INTENT_KEY = 'download-gate-intent';
export const DOWNLOAD_GATE_OPEN_KEY = 'download-gate-open';

/** Names this gate on the params a social redirect appends. */
export const DOWNLOAD_GATE_AUTH_SOURCE = 'productFiles';

discardStaleGateOpen(
    DOWNLOAD_GATE_OPEN_KEY,
    DOWNLOAD_GATE_INTENT_KEY,
    DOWNLOAD_GATE_AUTH_SOURCE,
);

export const downloadGateIntentAtom =
    atomWithStorage<DownloadGateIntent | null>(
        DOWNLOAD_GATE_INTENT_KEY,
        null,
        createJSONStorage<DownloadGateIntent | null>(() => sessionStorage),
    );

export const downloadGateOpenAtom = atomWithStorage<boolean>(
    DOWNLOAD_GATE_OPEN_KEY,
    false,
    createJSONStorage<boolean>(() => sessionStorage),
);

/** Read-only access for surfaces that only need to know an intent is pending. */
export const useDownloadGateIntent = () => useAtomValue(downloadGateIntentAtom);

export interface UseDownloadGate {
    intent: DownloadGateIntent | null;
    isOpen: boolean;
    /** Stop the reader at the gate, remembering what they were after. */
    open: (intent: DownloadGateIntent) => void;
    /** Close and forget — the intent must not outlive a dismissal. */
    close: () => void;
}

export const useDownloadGate = (): UseDownloadGate => {
    const [intent, setIntent] = useAtom(downloadGateIntentAtom);
    const [isOpen, setIsOpen] = useAtom(downloadGateOpenAtom);

    const open = useCallback(
        (next: DownloadGateIntent) => {
            setIntent(next);
            setIsOpen(true);
        },
        [setIntent, setIsOpen],
    );

    const close = useCallback(() => {
        setIsOpen(false);
        setIntent(null);
    }, [setIntent, setIsOpen]);

    return { intent, isOpen, open, close };
};

/** Clears a pending intent without touching the modal (post-download cleanup). */
export const useClearDownloadGateIntent = () => {
    const setIntent = useSetAtom(downloadGateIntentAtom);
    return useCallback(() => setIntent(null), [setIntent]);
};
